import { FormControl, FormDescription, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { OperatorForm } from "./operator.schema";
import { Input } from "@/src/components/ui/input";
import { PercentIcon, TrashIcon } from "lucide-react";
import { Fragment } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { Button } from "@/src/components/ui/button";
import { useFormContext } from "react-hook-form";
import { objectId } from "@/src/lib/object-id";

export function Payments() {
  const form = useFormContext<OperatorForm>();
  const paymentSchedule = form.watch("operatorPayments.paymentSchedule");

  return (
    <>
      <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(150px,328px))]">
        <h3 className="text-lg font-semibold col-span-full">Operators payments</h3>
        <FormField<OperatorForm, "operatorPayments.commission">
          name="operatorPayments.commission"
          render={({ field: { value, onChange, ...props } }) => (
            <FormItem required>
              <FormLabel>Commission</FormLabel>
              <FormControl>
                <Input
                  {...props}
                  defaultValue={value?.type === "percentage" ? `${value.value}%` : value?.value}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    if (value.length === 0) {
                      onChange(null);
                      return;
                    }
                    if (value[value.length - 1] === "%") {
                      onChange({
                        type: "percentage",
                        value: +value.slice(0, -1),
                      });
                    } else {
                      onChange({
                        type: "fixed",
                        value: +value,
                      });
                    }
                  }}
                />
              </FormControl>
              <FormDescription>Enter amount/percentage</FormDescription>
            </FormItem>
          )}
        />
        <FormField<OperatorForm, "operatorPayments.customDiscount">
          name="operatorPayments.customDiscount"
          render={({ field: { value, onChange, ...props } }) => (
            <FormItem>
              <FormLabel>Custom discount</FormLabel>
              <FormControl>
                <Input
                  suffixIcon={<PercentIcon />}
                  {...props}
                  onChange={(e) => {
                    const value = +e.target.value;
                    onChange(value);
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(150px,328px))]">
        <h3 className="text-lg font-semibold col-span-full">Payment schedule</h3>
        {paymentSchedule.map((step, i) => (
          <Fragment key={objectId(step)}>
            <div className="flex gap-2 col-start-1 items-end w-full">
              <FormField<OperatorForm, `operatorPayments.paymentSchedule.${number}.amount`>
                name={`operatorPayments.paymentSchedule.${i}.amount`}
                render={({ field: { value, onChange, ...props } }) => (
                  <FormItem required>
                    <FormLabel>Amount {i + 1}</FormLabel>
                    <FormControl>
                      <Input
                        {...props}
                        value={step.amount}
                        suffixIcon={<PercentIcon />}
                        onChange={(e) => {
                          const value = +e.target.value;
                          onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField<OperatorForm, `operatorPayments.paymentSchedule.${number}.days`>
                name={`operatorPayments.paymentSchedule.${i}.days`}
                render={({ field: { value, onChange, ...props } }) => (
                  <FormItem required>
                    <FormLabel>Days</FormLabel>
                    <FormControl>
                      <Input
                        {...props}
                        value={step.days}
                        disabled={step.type === "on confirmation"}
                        onChange={(e) => {
                          const value = +e.target.value;
                          onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <FormField<OperatorForm, `operatorPayments.paymentSchedule.${number}.type`>
              name={`operatorPayments.paymentSchedule.${i}.type`}
              render={({ field: { value, onChange } }) => (
                <FormItem required>
                  <FormLabel>Payment time</FormLabel>
                  <div className="flex gap-2 items-end w-full">
                    <Select onValueChange={onChange} value={step.type}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="on confirmation">on confirmation</SelectItem>
                        <SelectItem value="after confirmation">after confirmation</SelectItem>
                        <SelectItem value="before booking date">before charter date</SelectItem>
                      </SelectContent>
                    </Select>
                    {paymentSchedule.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          form.setValue(
                            `operatorPayments.paymentSchedule`,
                            paymentSchedule.filter((_, idx) => idx !== i),
                          );
                        }}
                      >
                        <TrashIcon size={16} />
                      </Button>
                    )}
                  </div>
                </FormItem>
              )}
            />
          </Fragment>
        ))}
        <Button
          onClick={() => {
            form.setValue(
              `operatorPayments.paymentSchedule`,
              [
                ...paymentSchedule,
                {
                  amount: null,
                  days: null,
                } as any,
              ],
              {
                shouldDirty: true,
              },
            );
          }}
          className="col-start-1"
          type="button"
          variant="secondary"
        >
          Add another step
        </Button>
      </div>
    </>
  );
}
